import { IBaseCartGoodsItem } from '@/api/types/cart';
import { Currency, IPrice, User } from '@/api/types/public';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { add, subtract } from '@/utils/math';
import {
  COATING_COMPONENT,
  EDIT_PRESCRIPTION_COMPONENT,
  LENS_INDEX_COMPONENT,
  LENS_TYPE_COMPONENT,
  LENS_VIEW_AREA_COMPONENT,
  PRESCRIPTION_TYPE_COMPONENT,
  ZL_PRESCRIPTION_DATA,
} from './constants';
import { CoatingItem, ColorItem, LensIndexItem } from './types';

export type ApplyMembershipLevel = -1 | 0 | 1 | 2 | 3;

export type PromotionDisplayInfo = {
  applyMembershipLevel: ApplyMembershipLevel;
  promotionDisplayLabel: string;
};

const EMPTY_PRICE_INFO = {
  price: '0.00',
  sell_price: '0.00',
};

const normalizePromotionText = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  return String(value).trim();
};

const normalizeApplyMembershipLevel = (value: unknown): ApplyMembershipLevel => {
  const level = typeof value === 'string' ? Number(value) : value;

  return level === -1 || level === 0 || level === 1 || level === 2 || level === 3 ? level : -1;
};

const getPromotionDisplayValue = (source: unknown, key: string) => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  return (source as Record<string, unknown>)[key];
};

export const resolvePromotionDisplayInfo = (source?: unknown): PromotionDisplayInfo => ({
  applyMembershipLevel: normalizeApplyMembershipLevel(
    getPromotionDisplayValue(source, 'apply_membership_level'),
  ),
  promotionDisplayLabel: normalizePromotionText(
    getPromotionDisplayValue(source, 'promotion_display_label'),
  ),
});

export const resolvePromotionDisplayLabel = (source?: unknown): string =>
  resolvePromotionDisplayInfo(source).promotionDisplayLabel;

export const resolveOptionPromotionDisplayInfo = (
  option?: unknown,
  colorItem?: unknown,
): PromotionDisplayInfo => {
  const colorPromotionDisplayInfo = resolvePromotionDisplayInfo(colorItem);

  return colorPromotionDisplayInfo.promotionDisplayLabel
    ? colorPromotionDisplayInfo
    : resolvePromotionDisplayInfo(option);
};

export const resolveOptionPromotionDisplayLabel = (option?: unknown, colorItem?: unknown): string =>
  resolveOptionPromotionDisplayInfo(option, colorItem).promotionDisplayLabel;

const getOptionalColorPriceInfo = (
  colorItem: ColorItem | null | undefined,
  currency: Currency,
  userInfo: User,
) => {
  if (!colorItem?.lens_type_price_list?.length) {
    return EMPTY_PRICE_INFO;
  }

  return handleColorItemPriceInfo(colorItem, false, currency, userInfo);
};

export const getAdvancedLensUpgradePriceInfo = ({
  advancedLensIndexItem,
  currency,
  selectedColorItem,
  selectedLensIndexItem,
  userInfo,
}: {
  advancedLensIndexItem?: LensIndexItem | null;
  currency: Currency;
  selectedColorItem?: ColorItem | null;
  selectedLensIndexItem?: LensIndexItem | null;
  userInfo: User;
}) => {
  if (!advancedLensIndexItem || !selectedLensIndexItem) {
    return {
      applyMembershipLevel: -1,
      price: '0.00',
      promotionDisplayLabel: '',
      sellPrice: '0.00',
    };
  }

  const advancedIndexPrice = handleLensIndexPriceInfo(advancedLensIndexItem, currency, userInfo);
  const normalIndexPrice = handleLensIndexPriceInfo(selectedLensIndexItem, currency, userInfo);
  const advancedTypePrice = getOptionalColorPriceInfo(
    advancedLensIndexItem as unknown as ColorItem,
    currency,
    userInfo,
  );
  const normalTypePrice = getOptionalColorPriceInfo(selectedColorItem, currency, userInfo);

  const normalSellTotal = add(
    Number(normalIndexPrice.sell_price),
    Number(normalTypePrice.sell_price),
  );
  const advancedOriginalTotal = add(
    Number(advancedIndexPrice.price),
    Number(advancedTypePrice.price),
  );
  const advancedSellTotal = add(
    Number(advancedIndexPrice.sell_price),
    Number(advancedTypePrice.sell_price),
  );

  const upgradeOriginalPrice = subtract(advancedOriginalTotal, normalSellTotal);
  const upgradeSellPrice = subtract(advancedSellTotal, normalSellTotal);

  const advancedPromotionDisplayInfo = resolvePromotionDisplayInfo(advancedLensIndexItem);

  return {
    applyMembershipLevel: advancedPromotionDisplayInfo.applyMembershipLevel,
    price: upgradeOriginalPrice.lt(0) ? '0.00' : upgradeOriginalPrice.toFixed(2),
    promotionDisplayLabel: advancedPromotionDisplayInfo.promotionDisplayLabel,
    sellPrice: upgradeSellPrice.lt(0) ? '0.00' : upgradeSellPrice.toFixed(2),
  };
};

// 数字小数点后补齐2位
export const getToFix2NumArr = (arr: string[]) => {
  return arr.map((item) => {
    const a = item.split('.');
    a.length === 1 ? a.push('00') : a[1].length === 1 && (a[1] += '0');
    return `+${a.join('.')}`;
  });
};

// 生成等差数组
export const getEqualDiffArr = (start: number, end: number, difference: number) => {
  if (((end - start) / difference) % 1 > 0) {
    return [];
  }
  return new Array((end - start) / difference + 1).fill(0).map((_, index) => {
    return String(start + difference * index);
  });
};

/**
 * @description: 价格计算处理
 * hasIndexPrice 为真时（frame only 或者 non-prescription 或者 sunglasses non-prescription）
 * 价格为 “镜片类型组合价” + “镜片折射率组合价”，其中镜片折射率组合价是，color_list下的lens_list的第一项中的index_price_list
 * 否则，价格为 “镜片类型价”
 * @return {*}
 */
export const handleColorItemPriceInfo = (
  colorItem: ColorItem,
  hasIndexPrice = false,
  currency: Currency,
  userInfo: User,
) => {
  let lensPriceList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    colorItem[`lens_type_price_list_v${userInfo.member_level}`] &&
    colorItem[`discount_percent_v${userInfo.member_level}`] > 0
  ) {
    lensPriceList = colorItem[`lens_type_price_list_v${userInfo.member_level}`];
  } else {
    lensPriceList = colorItem.lens_type_price_list as IPrice[];
  }
  const lensGenPrice = lensPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: lensGenPrice.price,
    sell_price: lensGenPrice.sell_price,
  };
  if (Array.isArray(colorItem.lens_list) && colorItem.lens_list.length && hasIndexPrice) {
    const indexPrice = colorItem.lens_list[0]; // 镜片折射率组合价
    let indexPriceList: IPrice[] = [];
    if (
      userInfo?.is_member === 1 &&
      indexPrice[`index_price_list_v${userInfo.member_level}`] &&
      indexPrice[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      indexPriceList = indexPrice[`index_price_list_v${userInfo.member_level}`];
    } else {
      indexPriceList = indexPrice.index_price_list as IPrice[];
    }
    const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片折射率组合价
    const lensAddIndexPriceInfo = {
      currency: currency.name,
      price: add(Number(lensGenPrice.price), Number(indexGenPrice.price)).toFixed(2),
      sell_price: add(Number(lensGenPrice.sell_price), Number(indexGenPrice.sell_price)).toFixed(2),
    };
    return lensAddIndexPriceInfo; // 返回相加之后的价格
  }
  return priceInfo;
};

/**
 * 获取最小镜片价格所在的颜色item
 * @param colorList 镜片列表
 * @param hasIndexPrice 是否包含折射率价格
 * @param userInfo 用户信息
 * @returns 最小镜片价格
 */
export const getMinColorItem = (colorList: ColorItem[], hasIndexPrice = false, userInfo: User) => {
  let colorItemIndex = 0;
  if (hasIndexPrice) {
    colorItemIndex = minPrice(colorList, userInfo, 'lens_type_price_list', 'index_price_list') || 0;
  } else {
    colorItemIndex = minPrice(colorList, userInfo, 'lens_type_price_list') || 0;
  }

  return colorItemIndex === -1 ? {} : colorList[colorItemIndex] || {};
};

/**
 * 最小镜片价格下标
 */
export const minPrice = (itemList: ColorItem[], userInfo: User, key: string, key2?: string) => {
  if (itemList.length === 0) {
    return -1;
  }

  let minIndex = 0;
  let minValue = itemList[0];
  if (userInfo.member_level > -1) {
    key = `${key}_v${userInfo.member_level}`;
    if (key2) {
      key2 = `${key2}_v${userInfo.member_level}`;
    }
  }

  for (let i = 1; i < itemList.length; i++) {
    let curr = parseFloat(itemList[i][key][0].sell_price);
    let min = parseFloat(minValue[key][0].sell_price);
    if (key2) {
      curr += parseFloat(itemList[i].lens_list[0][key2][0].sell_price);
      min += parseFloat(minValue.lens_list[0][key2][0].sell_price);
    }
    if (curr < min) {
      minIndex = i;
      minValue = itemList[i];
    }
  }
  return minIndex;
};

/**
 * @description: 折扣 discount_percent计算处理
 * frame only 或者 non-prescription 或者 sunglasses non-prescription时
 * 价格为 “镜片类型组合价” + “镜片折射率组合价”，其中镜片折射率组合价是，color_list下的lens_list的第一项中的index_price_list
 * （（lensType原价 + lensIndex原价） - （lensType促销 + lensIndex促销））/ (lensType原价 + lensIndex原价 )
 * @return {*}
 */
export const getDiscountPercent = (
  colorItem: ColorItem,
  hasIndexPrice = false,
  currency: Currency,
  userInfo: User,
  isWap = false,
) => {
  if (hasIndexPrice) {
    let lensPriceList: IPrice[] = [];
    if (
      userInfo.is_member === 1 &&
      colorItem[`lens_type_price_list_v${userInfo.member_level}`] &&
      colorItem[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      lensPriceList = colorItem[`lens_type_price_list_v${userInfo.member_level}`];
    } else {
      lensPriceList = colorItem.lens_type_price_list as IPrice[];
    }
    const lensGenPrice = lensPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
    // （（lensType原价 + lensIndex原价） - （lensType促销 + lensIndex促销））/ (lensType原价 + lensIndex原价 )
    if (Array.isArray(colorItem.lens_list) && colorItem.lens_list.length) {
      const indexPrice = colorItem.lens_list[0]; // 镜片折射率组合价
      let indexPriceList: IPrice[] = [];
      if (
        userInfo.is_member === 1 &&
        indexPrice[`index_price_list_v${userInfo.member_level}`] &&
        indexPrice[`discount_percent_v${userInfo.member_level}`] > 0
      ) {
        indexPriceList = indexPrice[`index_price_list_v${userInfo.member_level}`];
      } else {
        indexPriceList = indexPrice.index_price_list as IPrice[];
      }
      const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片折射率组合价
      const lensAddIndexPriceInfo = {
        price: add(Number(lensGenPrice.price), Number(indexGenPrice.price)).toFixed(2),
        sell_price: add(Number(lensGenPrice.sell_price), Number(indexGenPrice.sell_price)).toFixed(
          2,
        ),
      };
      const discountPercent = Math.round(
        ((Number(lensAddIndexPriceInfo.price) - Number(lensAddIndexPriceInfo.sell_price)) /
          Number(lensAddIndexPriceInfo.price)) *
          100,
      );
      return discountPercent;
    }
  }
  if (userInfo.is_member === 1) {
    return colorItem[`display_discount_percentage_v${userInfo.member_level}`] || 0;
  } else {
    return isWap ? colorItem.discount_percent?.wap || 0 : colorItem.discount_percent?.pc || 0;
  }
};

// 是否应用了会员折扣
export const hasColorItemMemberPrice = (
  colorItem: ColorItem,
  userInfo: User,
  hasIndexPrice = false,
) => {
  let hasColorPrice = false;
  let hasLensIndex = false;
  if (userInfo.member_level === -1) {
    return false;
  }

  // 判断镜片类型是否有会员价
  if (colorItem && colorItem[`discount_percent_v${userInfo.member_level}`] > 0) {
    hasColorPrice = true;
  }

  if (hasIndexPrice && colorItem) {
    const lensIndexItem = colorItem.lens_list[0];
    if (lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0) {
      hasLensIndex = true;
    }
    return hasColorPrice || hasLensIndex;
  }

  return hasColorPrice;
};

/**
 * 处理镜片折射率价格信息
 * @param lensIndexItem 镜片折射率项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleLensIndexPriceInfo = (
  lensIndexItem: LensIndexItem,
  currency: Currency,
  userInfo: User,
) => {
  let indexPriceList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    lensIndexItem[`index_price_list_v${userInfo.member_level}`]
  ) {
    indexPriceList = lensIndexItem[`index_price_list_v${userInfo.member_level}`];
  } else {
    indexPriceList = lensIndexItem.index_price_list as IPrice[];
  }
  const indexGenPrice = indexPriceList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: indexGenPrice.price,
    sell_price: indexGenPrice.sell_price,
  };
  return priceInfo;
};

/**
 * 处理镜片折射率价格折扣
 * @param lensIndexItem 镜片折射率项
 * @param userInfo 用户信息
 * @returns 价格折扣
 */
export const handleLensIndexDiscountPercent = (lensIndexItem: LensIndexItem, userInfo: User) => {
  if (lensIndexItem.index_price_discount_type === 1) {
    if (
      userInfo.is_member === 1 &&
      lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`] &&
      lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`] > 0
    ) {
      return lensIndexItem[`display_discount_percentage_v${userInfo.member_level}`];
    } else {
      return lensIndexItem.display_discount_percentage || 0;
    }
  } else if (
    userInfo.is_member === 1 &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] &&
    lensIndexItem[`discount_percent_v${userInfo.member_level}`] > 0
  ) {
    return lensIndexItem[`discount_percent_v${userInfo.member_level}`];
  } else {
    return 0;
  }
};

/**
 * 处理镀膜价格信息
 * @param coatingItem 镀膜项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleCoatingPriceInfo = (
  coatingItem: CoatingItem,
  currency: Currency,
  userInfo: User,
) => {
  let priceInfoList: IPrice[] = [];
  if (
    userInfo?.is_member === 1 &&
    coatingItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    coatingItem[`price_info_v${userInfo.member_level}`]
  ) {
    priceInfoList = coatingItem[`price_info_v${userInfo.member_level}`];
  } else {
    priceInfoList = coatingItem.price_info as IPrice[];
  }
  const priceInfoItem = priceInfoList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: priceInfoItem.price,
    sell_price: priceInfoItem.sell_price,
  };
  return priceInfo;
};

/** 配镜步骤全局顺序（用于 hasReachedLensProcessStep） */
const LENS_PROCESS_COMPONENT_RANK: Record<string, number> = {
  [PRESCRIPTION_TYPE_COMPONENT]: 0,
  [EDIT_PRESCRIPTION_COMPONENT]: 1,
  [LENS_TYPE_COMPONENT]: 2,
  [LENS_INDEX_COMPONENT]: 3,
  [LENS_VIEW_AREA_COMPONENT]: 4,
  [COATING_COMPONENT]: 5,
};

/** 当前步骤是否已达到或超过目标步骤（未识别步骤视为未达到） */
export function hasReachedLensProcessStep(nowComponent: string, targetComponent: string): boolean {
  const nowRank = LENS_PROCESS_COMPONENT_RANK[nowComponent];
  const targetRank = LENS_PROCESS_COMPONENT_RANK[targetComponent];
  if (nowRank == null || targetRank == null) return false;
  return nowRank >= targetRank;
}

/**
 * 按当前流程步骤下标判断是否计入小计（返回上一步后 nowStep 会变小，后续步骤价格应排除）。
 */
export function hasReachedStepInCurrentFlow(
  allComponent: string[],
  nowStep: number,
  targetComponent: string,
): boolean {
  const targetIndex = allComponent.indexOf(targetComponent);
  if (targetIndex === -1) return false;
  return nowStep >= targetIndex;
}

/** 按当前步骤可见性累加小计（与 useShowLensInfo / LensInfo 展示规则一致） */
export interface LensProcessPriceParts {
  goodsFramePriceInfo: IPrice;
  lensTypePriceInfo?: IPrice;
  lensIndexPriceInfo?: IPrice;
  coatingPriceInfo?: IPrice;
}

export interface LensProcessTotalPriceVisibility {
  showLens?: boolean;
  showLensIndex?: boolean;
  showCoating?: boolean;
}

export function sumLensProcessTotalPrice(
  parts: LensProcessPriceParts,
  visibility: LensProcessTotalPriceVisibility,
  currencyName: string,
): IPrice {
  const frame = parts.goodsFramePriceInfo;
  let sell = frame.sell_price;
  let list = frame.price ?? frame.sell_price;

  if (visibility.showLens && parts.lensTypePriceInfo?.sell_price) {
    sell = add(sell, parts.lensTypePriceInfo.sell_price).toFixed(2);
    list = add(list, parts.lensTypePriceInfo.price ?? parts.lensTypePriceInfo.sell_price).toFixed(
      2,
    );
  }
  if (visibility.showLensIndex && parts.lensIndexPriceInfo?.sell_price) {
    sell = add(sell, parts.lensIndexPriceInfo.sell_price).toFixed(2);
    list = add(list, parts.lensIndexPriceInfo.price ?? parts.lensIndexPriceInfo.sell_price).toFixed(
      2,
    );
  }
  if (visibility.showCoating && parts.coatingPriceInfo?.sell_price) {
    sell = add(sell, parts.coatingPriceInfo.sell_price).toFixed(2);
    list = add(list, parts.coatingPriceInfo.price ?? parts.coatingPriceInfo.sell_price).toFixed(2);
  }

  return {
    currency: currencyName,
    sell_price: sell,
    price: list,
  };
}

/**
 * 当前配镜流程是否包含镀膜步骤（与 ClientPage 拼装 `allComponent` 一致）。
 * 未选颜色时按「可能需要镀膜」占位；选色后由 `is_coating` 决定是否保留该步。
 */
export function isCoatingStepInFlow(
  prescriptionType: EPrescriptionType | string | null | undefined,
  selectedColorItem: ColorItem | null | undefined,
  options?: { isFrameProcess?: boolean },
): boolean {
  const colorAllowsCoating = !selectedColorItem || selectedColorItem.is_coating === 1;
  if (options?.isFrameProcess) {
    return colorAllowsCoating;
  }
  const isNonPrescriptionFlow = [
    EPrescriptionType.NON_PRESCRIPTION,
    EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION,
  ].includes(prescriptionType as EPrescriptionType);
  return isNonPrescriptionFlow && colorAllowsCoating;
}

/**
 * 是否应将镀膜写入计价/加购/localStorage 默认勾选。
 * 在 `isCoatingStepInFlow` 基础上要求已选颜色且 `is_coating === 1`；
 * 未选色时流程可占位镀膜步，但不应提前写入镀膜数据。
 */
export function shouldApplyCoatingSelection(
  prescriptionType: EPrescriptionType | string | null | undefined,
  selectedColorItem: ColorItem | null | undefined,
  options?: { isFrameProcess?: boolean },
): boolean {
  if (!selectedColorItem || selectedColorItem.is_coating !== 1) {
    return false;
  }
  return isCoatingStepInFlow(prescriptionType, selectedColorItem, options);
}

/**
 * 与 `useApplyDefaultSelection.initDefaultCoatingSelection` 一致：优先按 localStorage 的 `prevCoatingId` 恢复，否则取售价最高的项作兜底。
 * 供「进入镀膜步骤」时在没有已选项的情况下与接口首次默认逻辑保持一致。
 */
export function pickDefaultCoatingItem(
  coatingList: CoatingItem[],
  currency: Currency | null,
  userInfo: User,
  prevCoatingId: string,
): CoatingItem | null {
  if (!coatingList?.length || !currency) return null;
  let pick = prevCoatingId ? coatingList.find((c) => `${c.id}` === prevCoatingId) : undefined;
  if (!pick) {
    pick = coatingList.reduce((best, c) => {
      const p = parseFloat(handleCoatingPriceInfo(c, currency, userInfo).sell_price || '0');
      const bp = parseFloat(handleCoatingPriceInfo(best, currency, userInfo).sell_price || '0');
      return p > bp ? c : best;
    }, coatingList[0]);
  }
  return pick ?? null;
}

// 镀膜是否应用了会员折扣
export const hasCoatingMemberPrice = (coatingItem: CoatingItem, userInfo: User) => {
  let hasColorPrice = false;
  if (userInfo.member_level === -1) {
    return false;
  }

  // 判断镜片类型是否有会员价
  if (coatingItem && coatingItem[`discount_percent_v${userInfo.member_level}`] > 0) {
    hasColorPrice = true;
  }

  return hasColorPrice;
};

/**
 * 处理镀膜价格折扣
 * @param coatingItem 镀膜项
 * @param userInfo 用户信息
 * @returns 价格折扣
 */
export const handleCoatingDiscountPercent = (coatingItem: CoatingItem, userInfo: User) => {
  if (userInfo.is_member === 1 && coatingItem[`discount_percent_v${userInfo.member_level}`]) {
    return coatingItem[`discount_percent_v${userInfo.member_level}`];
  }
  return 0;
};

/**
 * 处理商品镜框价格信息
 * @param coatingItem 镀膜项
 * @param currency 币种
 * @param userInfo 用户信息
 * @returns 价格信息
 */
export const handleGoodsFramePriceInfo = (
  goodsFrameItem: IBaseCartGoodsItem['product'] & {
    point_price_info?: IPrice[];
  },
  isRedeem = false,
  currency: Currency,
  userInfo: User,
) => {
  let priceInfoList: IPrice[] = [];
  if (isRedeem && goodsFrameItem.point_price_info) {
    priceInfoList = goodsFrameItem.point_price_info;
  } else if (
    userInfo?.is_member === 1 &&
    goodsFrameItem[`discount_percent_v${userInfo.member_level}`] > 0 &&
    goodsFrameItem[`price_info_v${userInfo.member_level}`]
  ) {
    priceInfoList = goodsFrameItem[`price_info_v${userInfo.member_level}`];
  } else {
    priceInfoList = goodsFrameItem.price_info as IPrice[];
  }
  const priceInfoItem = priceInfoList.find((item) => item.currency === currency.name); // 当前币种下的镜片类型组合价
  const priceInfo = {
    currency: currency.name,
    price: priceInfoItem.price,
    sell_price: priceInfoItem.sell_price,
  };
  return priceInfo;
};

/**
 * 处理处方数据
 * @param data 前端处方数据
 * @returns 可提交给接口的处方数据
 */
export const handlerPrescriptionData = (data) => {
  const presData = { ...data };
  const option = {};
  if (presData.option) {
    const excludeKey = [
      'coating_id',
      'lens_id',
      'old_lens_id',
      'pd',
      'pd_l',
      'pd_r',
      'od_axis',
      'os_axis',
      'name',
      'old_lens_price',
      'old_lens_status',
    ]; // 不需要转换
    const keys = Object.keys(presData.option);
    for (const key of keys) {
      if (isNaN(parseFloat(presData.option[key])) || excludeKey.includes(key)) {
        option[key] = `${presData.option[key]}`;
      } else {
        option[key] = `${parseFloat(presData.option[key]).toFixed(2)}`;
      }
    }
    presData.option = option;
  }
  return presData;
};

export const removePrescriptionPlus = (prescription: IPrescriptionOptions) => {
  const excludeKey = [
    'coating_id',
    'lens_id',
    'old_lens_id',
    'pd',
    'pd_l',
    'pd_r',
    'od_axis',
    'os_axis',
    'name',
    'old_lens_price',
    'old_lens_status',
  ]; // 不需要转换
  const keys = Object.keys(prescription);
  for (const key of keys) {
    if (isNaN(parseFloat(prescription[key] as string)) || excludeKey.includes(key)) {
      prescription[key] = `${prescription[key]}`;
    } else {
      prescription[key] = `${parseFloat(prescription[key] as string).toFixed(2)}`;
    }
  }
  return prescription;
};

export const getApiPrescriptionType = (prescriptionType: EPrescriptionType) => {
  return [EPrescriptionType.READERS, EPrescriptionType.PRESCRIPTION_READING].includes(
    prescriptionType,
  )
    ? EPrescriptionType.READING_GLASSES
    : prescriptionType;
};

// 比较当前的处方和识别出的处方是否相同
export const preIsSame = (newPres: IPrescriptionOptions, oldPres: IPrescriptionOptions) => {
  const defaultPres = {
    od_sph: '0.00',
    os_sph: '0.00',
    od_cyl: '0.00',
    os_cyl: '0.00',
    od_axis: 'None',
    os_axis: 'None',
    od_add: '0.00',
    os_add: '0.00',
    // 两组PD
    hasTwoPd: false,
    pd: '',
    pd_l: '',
    pd_r: '',
    // 是否具有斜视数据
    hasStrabismus: false,
    od_pv: '0.00',
    od_bd: '',
    os_pv: '0.00',
    os_bd: '',
    od_pv_r: '0.00',
    od_bd_r: '',
    os_pv_r: '0.00',
    os_bd_r: '',
    saveMyPres: false,
    name: '',
    prescriptions_image: '', // 处方单图片
  };
  const keysNewPres = [
    'od_sph',
    'os_sph',
    'od_cyl',
    'os_cyl',
    'od_axis',
    'os_axis',
    'od_add',
    'os_add',
    'pd',
    'pd_l',
    'pd_r',
    // 'od_pv', 'od_bd', 'os_pv', 'os_bd', 'od_pv_r', 'od_bd_r', 'os_pv_r', 'os_bd_r',
  ];
  // 检查每个键的值是否相同
  for (const key of keysNewPres) {
    if (
      newPres[key] !== oldPres[key] &&
      +newPres[key] !== +oldPres[key] &&
      oldPres[key] !== defaultPres[key]
    ) {
      return false;
    }
  }
  return true;
};

// 获取缓存的成品处方数据
export const getReaderCachePres = () => {
  if (typeof window === 'undefined') return '';

  const key = ZL_PRESCRIPTION_DATA;
  const cacheValue = localStorage.getItem(key);
  try {
    if (cacheValue) {
      const data = JSON.parse(cacheValue);
      if (data[EPrescriptionType.READERS]) {
        const sph = data[EPrescriptionType.READERS].od_sph;
        return sph;
      }
    }
  } catch (e) {
    localStorage.removeItem(key);
    logger.warn('👀 - getCachePres', e);
  }
  return '';
};
// 设置成品老化处方缓存
export const setReaderCachePres = (prescription: {
  od_sph: string;
  os_sph: string;
  presType: string;
}) => {
  if (typeof window === 'undefined') return;

  const key = ZL_PRESCRIPTION_DATA;
  const cacheValue = localStorage.getItem(key);
  try {
    const data = cacheValue ? JSON.parse(cacheValue) : {};
    data[EPrescriptionType.READERS] = prescription;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    localStorage.removeItem(key);
    logger.warn('👀 - setCachePres', e);
  }
};
